import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  listingName: string;
  vertical: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ReportListingModal({ listingName, vertical, open, onOpenChange }: Props) {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!details.trim()) return;
    setSending(true);
    try {
      await supabase.functions.invoke("contact-submit", {
        body: {
          full_name: email || "Anonymous",
          email: email || "report@flexaura.me",
          phone_number: null,
          message: `[Report Outdated Listing] ${vertical} — "${listingName}": ${details.trim()}`,
        },
      });
      toast({ title: "Thanks for letting us know!", description: "We'll review this listing shortly." });
      onOpenChange(false);
      setDetails("");
      setEmail("");
    } catch {
      toast({ title: "Something went wrong", description: "Please try again later.", variant: "destructive" });
    } finally {
      setSending(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Report outdated listing</DialogTitle>
          <DialogDescription>
            Help us keep listings accurate. Let us know what's changed about "{listingName}".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            placeholder="What's incorrect or outdated?"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={500}
            required
          />
          <Input
            type="email"
            placeholder="Your email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={sending || !details.trim()}>
              {sending ? "Sending..." : "Submit Report"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
